/**
 * AgentTypeView - In-panel "Add an agent" picker.
 *
 * Low-friction two-step flow inside the picker panel — every meaningful
 * choice commits on a single click:
 *   1. Choose agent type   — clicking a card immediately advances to step 2
 *   2. Choose model connection — filtered to the connections valid for the
 *      chosen variant (native → AzureOpenAI/OpenAI; foundry → FoundryAgentService[V2]).
 *      Clicking a row submits; "Autoselect connection" submits without a
 *      pre-selected ref so the picker logic chooses one.
 *
 * Maps user-facing names to the same operation shapes the rest of the picker
 * uses when expanding the base Agent operation:
 *   - "Workflow agent"        → native agent
 *   - "Foundry prompt agent"  → Foundry agent
 */

import { useMemo, useState } from 'react';
import {
  Button,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  ArrowLeftRegular,
  ArrowRight16Regular,
  BrainCircuitRegular,
  ChevronRightRegular,
  PlugConnectedRegular,
  SparkleFilled,
  StarFilled,
} from '@fluentui/react-icons';
import { useConnectionStore } from '@/stores/connectionStore';
import { useRegisterCreatedConnection } from '@/hooks/useConnectionMutations';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { isAgentConnectionId, resolveCreationConnectorName } from '@/utils/connections';
import {
  ConnectionCreationPanel,
} from '../NodeEditor/dialogs/ConnectionCreationPanel';
import type { AgentModelType } from '@/types/agentConnection';
import {
  getAgentReferenceProvider,
  getAllowedAgentProviders,
  FOUNDRY_AGENT_PROVIDERS,
  type AgentModelProvider,
} from '../workflowHelpers';
import type { ConnectionReference } from '@/types/connectionReference';
import {
  AgentVariantIcon,
  useAgentBadgeClass,
  type AgentOperationSelection,
  type AgentVariant,
} from '../helpers/agentVariant';

// Re-exported so existing call sites (`import type { AgentVariant } from './AgentTypeView'`)
// keep compiling. New code should import from `helpers/agentVariant`.
export type { AgentVariant };

// Alias kept so the picker exposes a stable name; structurally equal to AgentOperationSelection.
export type AgentSelection = AgentOperationSelection;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingBlock: tokens.spacingVerticalS,
    paddingInline: 0,
    minHeight: 0,
    flex: 1,
  },
  // Hero header — sets the tone for the picker without echoing the panel
  // title ("Add an agent"). The teal→purple gradient previews the two
  // accent hues used by the variant cards (and by the rendered agent nodes
  // on the canvas — see FloatingAgentNode).
  hero: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: tokens.borderRadiusXLarge,
    padding: tokens.spacingVerticalL,
    paddingInline: tokens.spacingHorizontalL,
    backgroundImage: `linear-gradient(135deg, ${tokens.colorPaletteTealBackground2} 0%, ${tokens.colorPalettePurpleBackground2} 100%)`,
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  heroIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase600,
    boxShadow: tokens.shadow4,
    flexShrink: 0,
  },
  heroBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  heroTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase500,
    color: tokens.colorNeutralForeground1,
  },
  heroSubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  stepHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  stepHeadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  stepLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  stepHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  // Variant card — large, gradient-accented, with chevron affordance.
  card: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalL,
    paddingInline: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    fontFamily: 'inherit',
    overflow: 'hidden',
    transitionProperty: 'background-color, border-color, box-shadow, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
      boxShadow: tokens.shadow8,
      transform: 'translateY(-1px)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':hover': {
        transform: 'none',
      },
    },
  },
  // Decorative gradient swatch on the right edge of each card.
  cardAccent: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 0.5,
    background:
      'radial-gradient(120% 100% at 100% 0%, var(--card-accent, transparent) 0%, transparent 60%)',
  },
  // Two distinct accent hues that match the canvas convention: teal for
  // native (workflow) agents, purple for Foundry agents. See
  // FloatingAgentNode (canvas), NodeEditorHeader, SubWorkflowPreview, and
  // FoundryDetailsTab — all use the same pairing.
  cardAccentNative: {
    '--card-accent': tokens.colorPaletteTealBackground2,
  } as Record<string, string>,
  cardAccentFoundry: {
    '--card-accent': tokens.colorPalettePurpleBackground2,
  } as Record<string, string>,
  cardAccentCoding: {
    '--card-accent': tokens.colorPaletteGreenBackground2,
  } as Record<string, string>,
  cardAccentHosted: {
    '--card-accent': tokens.colorPaletteMarigoldBackground2,
  } as Record<string, string>,
  iconWrapper: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: tokens.borderRadiusLarge,
    flexShrink: 0,
    fontSize: tokens.fontSizeBase600,
    boxShadow: tokens.shadow2,
  },
  cardBody: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
    flex: 1,
  },
  cardTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  cardTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  cardDesc: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
  },
  cardFeatures: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
  },
  featureChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingBlock: '2px',
    paddingInline: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusCircular,
  },
  newPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForegroundOnBrand,
    backgroundColor: tokens.colorPalettePurpleBorderActive,
    paddingBlock: '2px',
    paddingInline: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusCircular,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
  chevron: {
    position: 'relative',
    zIndex: 1,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
    transitionProperty: 'transform, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  // Step 2: connection list
  connList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  connRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    fontFamily: 'inherit',
    transitionProperty: 'background-color, border-color, transform, box-shadow',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
      boxShadow: tokens.shadow4,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  connIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForegroundOnBrand,
    flexShrink: 0,
  },
  connBody: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
    gap: tokens.spacingVerticalXXS,
  },
  connName: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  connMeta: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  autoselectButton: {
    alignSelf: 'flex-start',
  },
  // Compact "Create new connection" CTA in the connection list — mirrors the
  // ConnectionPicker create card so the affordance reads the same wherever a
  // user encounters it.
  createNewCard: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    backgroundColor: 'transparent',
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    fontFamily: 'inherit',
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  createNewIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
  },
  createNewBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
    flex: 1,
  },
  createNewTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  createNewMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  createNewArrow: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    paddingBlock: tokens.spacingVerticalXXL,
    paddingInline: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: tokens.fontSizeHero700,
    color: tokens.colorNeutralForeground4,
  },
  emptyTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  emptyDesc: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    maxWidth: '320px',
  },
});

type AgentConnectionOption = {
  refKey: string;
  ref: ConnectionReference;
  provider: AgentModelProvider;
  providerLabel: string;
};

const PROVIDER_LABELS: Record<AgentModelProvider, string> = {
  AzureOpenAI: 'Azure OpenAI',
  OpenAI: 'OpenAI',
  GitHubCopilot: 'GitHub Copilot',
  FoundryAgentService: 'Foundry Agent Service',
  FoundryAgentServiceV2: 'Foundry Agent Service V2',
};

// Default model type seeded into the inline create-connection flow when the
// user picks an agent variant without an existing connection. Mirrors the
// defaults applied by ConnectionTab when no parameter value is present.
const DEFAULT_CREATION_MODEL_TYPE: Record<AgentVariant, AgentModelType> = {
  native: 'AzureOpenAI',
  foundry: 'FoundryAgentService',
  coding: 'AzureOpenAI',
  hosted: 'AzureOpenAI', // Not used for connection creation — hosted agents use hostedAgentConnections
};

export interface AgentTypeViewProps {
  /** Called when the user cancels (clicks Cancel in the footer). */
  onCancel: () => void;
  /** Called with the agent variant and optional pre-selected connection ref. */
  onSubmit: (selection: AgentSelection) => void;
}

export const AgentTypeView = ({ onCancel: _onCancel, onSubmit }: AgentTypeViewProps) => {
  const styles = useStyles();
  const badgeClass = useAgentBadgeClass();
  const isHostedAgentEnabled = useFeatureFlag('hostedAgents');
  const references = useConnectionStore((state) => state.references);
  const registerCreatedConnection = useRegisterCreatedConnection();

  const [step, setStep] = useState<'type' | 'connection' | 'create-connection'>('type');
  const [variant, setVariant] = useState<AgentVariant>('native');

  const creationAgentModelType = DEFAULT_CREATION_MODEL_TYPE[variant];

  // Connections valid for the chosen variant. Both sides are strict:
  // foundry → only FoundryAgentService / V2; native → only AzureOpenAI /
  // OpenAI. Unclassified agent connections are intentionally hidden from
  // both lists so a Foundry connection without a `connectionProperties.type`
  // hint can't leak into the native picker (and vice versa). The empty
  // state below catches that case with a clear CTA.
  const connectionOptions = useMemo<AgentConnectionOption[]>(() => {
    const allowed = getAllowedAgentProviders(variant);
    return Object.entries(references).flatMap(([refKey, ref]) => {
      if (!isAgentConnectionId(ref.api?.id)) return [];
      const provider = getAgentReferenceProvider(ref);
      if (provider === undefined || !allowed.has(provider)) return [];
      return [{
        refKey,
        ref,
        provider,
        providerLabel: PROVIDER_LABELS[provider] ?? provider,
      }];
    });
  }, [references, variant]);

  const handlePickVariant = (next: AgentVariant) => {
    setVariant(next);
    // Hosted agents use hostedAgentConnections (separate from agentConnections),
    // so skip the model connection picker and submit directly.
    if (next === 'hosted') {
      onSubmit({ variant: next });
      return;
    }
    setStep('connection');
  };

  const handlePickConnection = (refKey: string) => {
    onSubmit({ variant, connectionRefName: refKey });
  };

  const handleSkipConnection = () => {
    onSubmit({ variant });
  };

  const handleBack = () => setStep('type');
  const handleStartCreate = () => setStep('create-connection');
  const handleCancelCreate = () => setStep('connection');

  // Submit the agent with the freshly created reference pre-selected. The
  // shared register hook handles store + query-cache wiring.
  const handleCreateComplete = (refKey: string, reference: ConnectionReference) => {
    registerCreatedConnection(refKey, reference);
    onSubmit({ variant, connectionRefName: refKey });
  };

  const renderTypeStep = () => (
    <>
      <div className={styles.hero}>
        <span className={styles.heroIcon} aria-hidden="true">
          <SparkleFilled />
        </span>
        <div className={styles.heroBody}>
          <Text as="h2" className={styles.heroTitle}>
            Choose an Agent
          </Text>
          <Text className={styles.heroSubtitle}>
            Agents reason over your data, call tools, and drive multi-step work inside a workflow.
          </Text>
        </div>
      </div>
      <div className={styles.cardList}>
        <button
          type="button"
          className={mergeClasses(styles.card, styles.cardAccentNative)}
          onClick={() => handlePickVariant('native')}
          aria-label="Workflow agent"
        >
          <span className={styles.cardAccent} aria-hidden="true" />
          <div className={mergeClasses(styles.iconWrapper, badgeClass('native'))} aria-hidden="true">
            <AgentVariantIcon variant="native" />
          </div>
          <div className={styles.cardBody}>
            <div className={styles.cardTitleRow}>
              <Text className={styles.cardTitle}>Workflow agent</Text>
              <span className={styles.featureChip}>Recommended</span>
            </div>
            <Text className={styles.cardDesc}>
              Build a custom agent natively in Logic Apps with full control over the agent
              loop, prompts, and context.
            </Text>
            <div className={styles.cardFeatures}>
              <span className={styles.featureChip}>Azure OpenAI</span>
              <span className={styles.featureChip}>Tools & memory</span>
              <span className={styles.featureChip}>Full control</span>
            </div>
          </div>
          <ChevronRightRegular className={styles.chevron} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={mergeClasses(styles.card, styles.cardAccentCoding)}
          onClick={() => handlePickVariant('coding')}
          aria-label="Coding agent"
        >
          <span className={styles.cardAccent} aria-hidden="true" />
          <div
            className={mergeClasses(styles.iconWrapper, badgeClass('coding'))}
            aria-hidden="true"
          >
            <AgentVariantIcon variant="coding" />
          </div>
          <div className={styles.cardBody}>
            <div className={styles.cardTitleRow}>
              <Text className={styles.cardTitle}>Coding agent</Text>
              <span className={styles.newPill}>
                <StarFilled aria-hidden="true" /> Preview
              </span>
            </div>
            <Text className={styles.cardDesc}>
              An agent that writes and runs code inside a sandboxed harness with cloned
              repositories, skills, and input files.
            </Text>
            <div className={styles.cardFeatures}>
              <span className={styles.featureChip}>Sandbox</span>
              <span className={styles.featureChip}>Skills</span>
              <span className={styles.featureChip}>File outputs</span>
            </div>
          </div>
          <ChevronRightRegular className={styles.chevron} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={mergeClasses(styles.card, styles.cardAccentFoundry)}
          onClick={() => handlePickVariant('foundry')}
          aria-label="Foundry prompt agent"
        >
          <span className={styles.cardAccent} aria-hidden="true" />
          <div
            className={mergeClasses(styles.iconWrapper, badgeClass('foundry'))}
            aria-hidden="true"
          >
            <AgentVariantIcon variant="foundry" />
          </div>
          <div className={styles.cardBody}>
            <div className={styles.cardTitleRow}>
              <Text className={styles.cardTitle}>Foundry prompt agent</Text>
              <span className={styles.newPill}>
                <StarFilled aria-hidden="true" /> New
              </span>
            </div>
            <Text className={styles.cardDesc}>
              A declarative agent defined through prompts, model selection, and tool
              configuration — no custom code needed.
            </Text>
            <div className={styles.cardFeatures}>
              <span className={styles.featureChip}>Foundry</span>
              <span className={styles.featureChip}>Prompt-first</span>
              <span className={styles.featureChip}>Zero code</span>
            </div>
          </div>
          <ChevronRightRegular className={styles.chevron} aria-hidden="true" />
        </button>
        {isHostedAgentEnabled && (
          <button
            type="button"
            className={mergeClasses(styles.card, styles.cardAccentHosted)}
            onClick={() => handlePickVariant('hosted')}
            aria-label="Foundry Hosted agent"
          >
            <span className={styles.cardAccent} aria-hidden="true" />
            <div
              className={mergeClasses(styles.iconWrapper, badgeClass('hosted'))}
              aria-hidden="true"
            >
              <AgentVariantIcon variant="hosted" />
            </div>
            <div className={styles.cardBody}>
              <div className={styles.cardTitleRow}>
                <Text className={styles.cardTitle}>Foundry Hosted agent</Text>
                <span className={styles.newPill}>
                  <StarFilled aria-hidden="true" /> Preview
                </span>
              </div>
              <Text className={styles.cardDesc}>
                A pre-provisioned agent running in a hosted container with input files,
                tools, and multi-step reasoning — connect to your Foundry project.
              </Text>
              <div className={styles.cardFeatures}>
                <span className={styles.featureChip}>Container</span>
                <span className={styles.featureChip}>Input files</span>
                <span className={styles.featureChip}>Foundry hosted</span>
              </div>
            </div>
            <ChevronRightRegular className={styles.chevron} aria-hidden="true" />
          </button>
        )}
      </div>
    </>
  );

  const variantTitle =
    variant === 'foundry'
      ? 'Foundry prompt agent'
      : variant === 'coding'
        ? 'Coding agent'
        : variant === 'hosted'
          ? 'Foundry Hosted agent'
          : 'Workflow agent';
  const emptyTitle =
    variant === 'foundry' ? 'No Foundry connections yet' : 'No model connections yet';
  const emptyDesc =
    variant === 'foundry'
      ? "You don't have any Foundry Agent Service connections. Add the agent and connect one from the editor."
      : "You don't have any Azure OpenAI or OpenAI connections. Add the agent and connect one from the editor.";

  const renderConnectionStep = () => (
    <>
      <div className={styles.stepHeader}>
        <div className={styles.stepHeadingRow}>
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowLeftRegular />}
            onClick={handleBack}
            aria-label="Back to agent type"
          />
          <Text className={styles.stepLabel}>Choose a model connection</Text>
        </div>
        <Text className={styles.stepHint}>
          {connectionOptions.length === 0
            ? `Adding ${variantTitle.toLowerCase()} — no compatible connections found.`
            : `Adding ${variantTitle.toLowerCase()}. Click a connection to add the agent.`}
        </Text>
      </div>
      {connectionOptions.length === 0 ? (
        <div className={styles.emptyState}>
          <PlugConnectedRegular className={styles.emptyIcon} aria-hidden="true" />
          <Text className={styles.emptyTitle}>{emptyTitle}</Text>
          <Text className={styles.emptyDesc}>{emptyDesc}</Text>
          <Button appearance="primary" icon={<AddRegular />} onClick={handleStartCreate}>
            Create new connection
          </Button>
          <Button appearance="subtle" onClick={handleSkipConnection}>
            Add agent without connection
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.connList} role="list" aria-label="Model connections">
            {connectionOptions.map((opt) => {
              const isFoundryProvider = FOUNDRY_AGENT_PROVIDERS.has(opt.provider);
              return (
                <button
                  type="button"
                  key={opt.refKey}
                  className={styles.connRow}
                  onClick={() => handlePickConnection(opt.refKey)}
                >
                  <span
                    className={mergeClasses(
                      styles.connIcon,
                      badgeClass(
                        isFoundryProvider ? 'foundry' : variant === 'coding' ? 'coding' : 'native',
                      ),
                    )}
                    aria-hidden="true"
                  >
                    <BrainCircuitRegular />
                  </span>
                  <span className={styles.connBody}>
                    <span className={styles.connName}>
                      {opt.ref.connectionName ?? opt.refKey}
                    </span>
                    <span className={styles.connMeta}>{opt.providerLabel}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className={styles.createNewCard}
            onClick={handleStartCreate}
            aria-label="Create new connection"
          >
            <span className={styles.createNewIcon} aria-hidden="true">
              <AddRegular />
            </span>
            <span className={styles.createNewBody}>
              <span className={styles.createNewTitle}>Create new connection</span>
              <span className={styles.createNewMeta}>
                Set up a new {variant === 'foundry' ? 'Foundry' : 'model'} connection
              </span>
            </span>
            <ArrowRight16Regular className={styles.createNewArrow} aria-hidden="true" />
          </button>
          <Button
            appearance="subtle"
            className={styles.autoselectButton}
            onClick={handleSkipConnection}
          >
            Autoselect connection
          </Button>
        </>
      )}
    </>
  );

  const renderCreateConnectionStep = () => {
    const creationConnectorName = resolveCreationConnectorName({
      isAgentOperation: true,
      agentModelType: creationAgentModelType,
      nodeType: 'Agent',
    });
    return (
      <ConnectionCreationPanel
        connectorType="Agent"
        {...(creationConnectorName ? { connectorName: creationConnectorName } : {})}
        agentModelType={creationAgentModelType}
        agentVariant={variant}
        onComplete={handleCreateComplete}
        onBack={handleCancelCreate}
      />
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        {step === 'type' && renderTypeStep()}
        {step === 'connection' && renderConnectionStep()}
        {step === 'create-connection' && renderCreateConnectionStep()}
      </div>
    </div>
  );
};

AgentTypeView.displayName = 'AgentTypeView';
